import React, { useMemo, useState } from "react";
import api from "../../../../api/client";
import AssetModal, { Field } from "./AssetModal";
import { RECORD_TYPES, errorMessage, machineLabel, toInputDateTime } from "./assetUtils";

const CHECK_OPTIONS = [
  { value: "ok", label: "OK" },
  { value: "not_ok", label: "Not OK" },
  { value: "na", label: "N/A" },
];

/** Filling a checklist. Answers are keyed by item id: { value, note }. */
export function ChecklistFill({ name, items, answers, onChange }) {
  const set = (id, patch) => onChange({ ...answers, [id]: { ...answers[id], ...patch } });
  return (
    <div className="am-checklist">
      <div className="am-checklist-title">✓ {name}</div>
      {items.map((item) => {
        const answer = answers[item.id] || {};
        return (
          <div className="am-checklist-item" key={item.id}>
            <span className="am-checklist-label">
              {item.label}
              {item.required && <span className="req" title="Required">*</span>}
            </span>
            {item.type === "check" ? (
              <span className="am-seg" role="group" aria-label={item.label}>
                {CHECK_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`${o.value}${answer.value === o.value ? " on" : ""}`}
                    aria-pressed={answer.value === o.value}
                    onClick={() => set(item.id, { value: o.value })}
                  >
                    {o.label}
                  </button>
                ))}
              </span>
            ) : item.type === "number" ? (
              <input
                className="am-input"
                style={{ width: 120 }}
                type="number"
                step="any"
                value={answer.value ?? ""}
                onChange={(e) => set(item.id, { value: e.target.value })}
                aria-label={item.label}
              />
            ) : (
              <span />
            )}
            {item.type === "text" ? (
              <input
                className="am-input am-checklist-note"
                value={answer.value ?? ""}
                onChange={(e) => set(item.id, { value: e.target.value })}
                aria-label={item.label}
              />
            ) : (
              // A "Not OK" is worth a word on what was wrong.
              answer.value === "not_ok" && (
                <input
                  className="am-input am-checklist-note"
                  placeholder="What was wrong? (optional)"
                  value={answer.note ?? ""}
                  onChange={(e) => set(item.id, { note: e.target.value })}
                />
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Log maintenance, or complete a preventive maintenance when `schedule` is
 * given — its checklist is then required, and the schedule moves on.
 */
export default function MaintenanceModal({ machines, machineId, schedule, checklists, people, onClose, onSaved }) {
  const [form, setForm] = useState({
    machineId: schedule?.machine?.id || machineId || "",
    type: schedule ? "preventive" : "inspection",
    performedAt: toInputDateTime(),
    description: schedule ? schedule.title : "",
    performer: "me",
    performedByName: "",
    cost: "",
    downtimeMinutes: "",
    meterReading: "",
    checklistId: "",
  });
  const [answers, setAnswers] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const checklist = useMemo(() => {
    if (schedule) return schedule.checklist;
    return checklists.find((c) => c.id === form.checklistId) || null;
  }, [schedule, checklists, form.checklistId]);

  const machine = machines.find((m) => m.id === form.machineId);
  const hourBased = schedule?.intervalType === "hours";

  const submit = async () => {
    if (!form.machineId) return setError("Choose a machine.");
    setBusy(true);
    setError(null);
    try {
      const data = new FormData();
      data.append("machineId", form.machineId);
      data.append("type", form.type);
      data.append("performedAt", new Date(form.performedAt).toISOString());
      data.append("description", form.description);
      if (form.performer === "other") data.append("performedByName", form.performedByName);
      else if (form.performer !== "me") data.append("performedById", form.performer);
      for (const key of ["cost", "downtimeMinutes", "meterReading"]) {
        if (form[key] !== "") data.append(key, form[key]);
      }
      if (schedule) data.append("scheduleId", schedule.id);
      else if (form.checklistId) data.append("checklistId", form.checklistId);
      if (checklist) data.append("checklistAnswers", JSON.stringify(answers));
      if (file) data.append("file", file);
      const res = await api.post("/manufacturing/assets/maintenance", data);
      onSaved(res.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save the maintenance record."));
      setBusy(false);
    }
  };

  return (
    <AssetModal
      title={schedule ? `Complete PM — ${schedule.title}` : "Log maintenance"}
      wide
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={schedule ? "Complete PM" : "Save record"}
    >
      {schedule ? (
        <div className="am-hint">
          {machineLabel(schedule.machine)} · completing this moves the schedule to its next due {hourBased ? "meter reading" : "date"}.
        </div>
      ) : (
        <div className="am-row">
          <Field label="Machine *">
            <select className="am-select" value={form.machineId} onChange={set("machineId")} required disabled={Boolean(machineId)}>
              <option value="">— Choose a machine —</option>
              {machines.map((m) => <option key={m.id} value={m.id}>{machineLabel(m)}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select className="am-select" value={form.type} onChange={set("type")}>
              {RECORD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
      )}

      <div className="am-row">
        <Field label="Done on">
          <input className="am-input" type="datetime-local" value={form.performedAt} max={toInputDateTime()} onChange={set("performedAt")} required />
        </Field>
        <Field label="Done by">
          <select className="am-select" value={form.performer} onChange={set("performer")}>
            <option value="me">Me</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            <option value="other">Outside vendor / someone else…</option>
          </select>
        </Field>
      </div>
      {form.performer === "other" && (
        <Field label="Vendor or person">
          <input className="am-input" value={form.performedByName} onChange={set("performedByName")} required maxLength={120} placeholder="e.g. Haas service engineer" />
        </Field>
      )}

      {!schedule && (
        <Field label="Checklist (optional)">
          <select className="am-select" value={form.checklistId} onChange={(e) => { set("checklistId")(e); setAnswers({}); }}>
            <option value="">— No checklist —</option>
            {checklists.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}
      {checklist && (
        <ChecklistFill name={checklist.name} items={checklist.items || []} answers={answers} onChange={setAnswers} />
      )}

      <Field label={schedule ? "Notes" : "Work done *"}>
        <textarea className="am-textarea" value={form.description} onChange={set("description")} required={!schedule} maxLength={2000} />
      </Field>

      <div className="am-row-3">
        <Field
          label={hourBased ? "Meter reading (hours) *" : "Meter reading (hours)"}
          hint={machine?.runningHours != null ? `Last: ${machine.runningHours}` : undefined}
        >
          <input className="am-input" type="number" min="0" step="0.1" value={form.meterReading} onChange={set("meterReading")} required={hourBased} />
        </Field>
        <Field label="Machine stopped for (min)">
          <input className="am-input" type="number" min="0" step="1" value={form.downtimeMinutes} onChange={set("downtimeMinutes")} />
        </Field>
        <Field label="Cost (₹)">
          <input className="am-input" type="number" min="0" step="0.01" value={form.cost} onChange={set("cost")} />
        </Field>
      </div>
      <Field label="Attachment (optional)" hint="Service report or photo — JPG, PNG, WebP or PDF">
        <input className="am-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} />
      </Field>
    </AssetModal>
  );
}
